import { isRoundPartType } from "../helpers/isRoundPartType";
import { isSquarePartType } from "../helpers/isSquarePartType";
import { PartTypeSchema, PartTypeType } from "../types";
import { round } from "../utils";

export type PartDimensions = {
  length: number;
  width: number;
  height: number;
  tubeThickness?: number;
};

export type DimensionsForComputation = {
  bbxdx: number;
  bbxdy: number;
  bbxdz: number;
  thickness: number | undefined;
  length: number | undefined;
  width: number | undefined;
  diameter: number | null | undefined;
};

export function computePartDimensions(
  dimensions: DimensionsForComputation,
  partType: PartTypeType | undefined | null,
): PartDimensions {
  const dx = dimensions.bbxdx;
  const dy = dimensions.bbxdy;
  const dz = dimensions.bbxdz;

  let length: number;
  let height: number;
  let width: number;

  if (partType === PartTypeSchema.enum.SHEET) {
    const featLength = dimensions.length ?? 0;
    const featWidth = dimensions.width ?? 0;
    length = round(Math.max(featLength, featWidth), 4);
    width = round(Math.min(featLength, featWidth), 4);
    height = round(dimensions.thickness ?? 0, 4);

    return {
      length,
      width,
      height,
    };
  }

  if (isRoundPartType(partType)) {
    const diameter = round(dimensions.diameter ?? 0, 4);
    const thickness = round(dimensions.thickness ?? 0, 4);
    const isPlainBar = diameter === thickness;

    const tubeThickness = isPlainBar ? undefined : thickness;
    width = diameter;
    height = diameter;
    length = round(dimensions.length ?? 0, 4);

    return {
      length,
      width,
      height,
      tubeThickness,
    };
  }

  if (isSquarePartType(partType)) {
    const pairs = [
      { diff: Math.abs(dx - dy), crossSection: [dx, dy], length: dz },
      { diff: Math.abs(dx - dz), crossSection: [dx, dz], length: dy },
      { diff: Math.abs(dy - dz), crossSection: [dy, dz], length: dx },
    ];

    const mostSimilarPair = pairs.reduce((min, current) =>
      current.diff < min.diff ? current : min,
    );

    length = round(mostSimilarPair.length, 4);
    height = round(Math.min(...mostSimilarPair.crossSection), 4);
    width = round(Math.max(...mostSimilarPair.crossSection), 4);

    const tubeThickness = dimensions.thickness ? round(dimensions.thickness, 4) : undefined;

    return {
      length,
      width,
      height,
      tubeThickness,
    };
  }

  length = round(Math.max(dx, dy, dz), 4);
  height = round(Math.min(dx, dy, dz), 4);
  width = round(dx + dy + dz - length - height, 4);

  const tubeThickness = dimensions.thickness ? round(dimensions.thickness, 4) : undefined;

  return {
    length,
    width,
    height,
    tubeThickness,
  };
}
